import { useEffect, useState } from "react";
import { Download, Eye, Loader2, Paperclip } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/ui/button";
import {
  fetchAttachments,
  type LogReviewAttachment,
} from "../../../api/logReviewPopupApi";
import { useToastStore } from "../../../stores/toastStore";
import {
  downloadFile,
  FileGlyph,
  FilePreviewModal,
  fileTypeMeta,
  formatBytes,
} from "../../components/files";

/** Minimal row shape — satisfied by both `LogReviewRow` and `LogInsightRow`. */
export interface AttachmentsRow {
  id: string;
  requestId: string;
  changeRequestId: string;
}

export interface AttachmentsModalProps {
  open: boolean;
  row: AttachmentsRow | null;
  onOpenChange: (open: boolean) => void;
}

/** Bordered, icon-only square action — matches the outline-box look. */
const IconAction = ({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Eye;
  label: string;
  onClick: () => void;
}) => (
  <Button
    variant="secondary"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="size-10 shrink-0 rounded-lg p-0"
  >
    <Icon className="size-[18px]" />
  </Button>
);

/**
 * Per-CR "Attachment(s)" popup. Fetches the supporting documents for a CR via
 * `getFilesByRequestId` and presents them as a file list — a coloured type
 * glyph, name + human-readable type · size, and icon-only Preview / Download
 * actions. Shared by all three Log Review dashboards.
 */
export const AttachmentsModal = ({ open, row, onOpenChange }: AttachmentsModalProps) => {
  const showError = useToastStore((s) => s.showError);
  const [files, setFiles] = useState<LogReviewAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<LogReviewAttachment | null>(null);

  useEffect(() => {
    if (!open || !row) return;
    const controller = new AbortController();
    setLoading(true);
    setFiles([]);
    fetchAttachments(row.id, row.requestId, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setFiles(rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(
          "Attachments",
          err instanceof Error ? err.message : "Failed to load attachments",
        );
        setLoading(false);
      });
    return () => controller.abort();
  }, [open, row, showError]);

  const subtitle = loading
    ? "Loading…"
    : `${files.length} ${files.length === 1 ? "file" : "files"} attached`;

  return (
    <>
      <Modal
        open={open}
        onClose={() => onOpenChange(false)}
        title={`Attachments (${row?.changeRequestId ?? ""})`}
        description={subtitle}
      >
        {loading ? (
          <div
            className="flex items-center justify-center py-12"
            data-testid="attachments-loading"
          >
            <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
          </div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <Paperclip className="size-9 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">No Data Available</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3 py-1" data-testid="attachments-list">
            {files.map((file, index) => {
              const { label } = fileTypeMeta(file.fileName, file.fileType);
              return (
                <li
                  key={`${file.fileName}-${index}`}
                  className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                >
                  <FileGlyph fileName={file.fileName} fileType={file.fileType} />

                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-semibold text-foreground"
                      title={file.fileName}
                    >
                      {file.fileName}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {label} &nbsp;•&nbsp; {formatBytes(file.fileSize)}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <IconAction
                      icon={Eye}
                      label={`Preview ${file.fileName}`}
                      onClick={() => setPreviewFile(file)}
                    />
                    <IconAction
                      icon={Download}
                      label={`Download ${file.fileName}`}
                      onClick={() => downloadFile(file)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onOpenChange={(o) => {
          if (!o) setPreviewFile(null);
        }}
      />
    </>
  );
};
